import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DateTime } from 'luxon'
import React, { useEffect, useMemo, useState } from 'react'
import { gql, useClient, useQuery } from 'urql'
import { useURLParams } from '../../actions/hooks'

const SERVICES_QUERY = gql`
  query ServicesQuery($search: String) {
    services(input: { first: 100, search: $search }) {
      nodes {
        id
        name
      }
    }
  }
`

const USERS_QUERY = gql`
  query UsersQuery($input: UserSearchOptions) {
    users(input: $input) {
      nodes {
        id
        name
      }
    }
  }
`

const USER_NAME_QUERY = gql`
  query UserNameQuery($id: ID!) {
    user(id: $id) {
      id
      name
    }
  }
`

const SERVICE_NAME_QUERY = gql`
  query ServiceNameQuery($id: ID!) {
    service(id: $id) {
      id
      name
    }
  }
`

const severityOptions = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]

export interface AlertMetricsFilterProps {
  serviceID?: string
  userID?: string
}

const filterOptions = (options: any[]) => options

export default function AlertMetricsFilter({ serviceID, userID }: AlertMetricsFilterProps): React.JSX.Element {
  const [params, setParams] = useURLParams({
    interval: 'P1D',
    services: '',
    users: '',
    severities: '',
    range: 'P1M',
    start: '',
    end: '',
  })

  // Destructure params for easier access
  const {
    interval: ivlParam,
    services: serviceIDParam,
    users: userIDParam,
    severities: severityParam,
    range: rangeParam,
    start: startParam,
    end: endParam,
  } = params as {
    interval: string
    services: string
    users: string
    severities: string
    range: string
    start: string
    end: string
  }

  // Local state for manual filtering
  const [interval, setInterval] = useState(ivlParam)
  const [serviceIDStr, setServiceIDStr] = useState(serviceIDParam)
  const [userIDStr, setUserIDStr] = useState(userIDParam)
  const [severityStr, setSeverityStr] = useState(severityParam)
  const [range, setRange] = useState(rangeParam)
  const [startStr, setStartStr] = useState(startParam)
  const [endStr, setEndStr] = useState(endParam)

  // Sync local state with URL params when they change (e.g. back button)
  useEffect(() => {
    setInterval(ivlParam)
    setServiceIDStr(serviceIDParam)
    setUserIDStr(userIDParam)
    setSeverityStr(severityParam)
    setRange(rangeParam)
    setStartStr(startParam)
    setEndStr(endParam)
  }, [ivlParam, serviceIDParam, userIDParam, severityParam, rangeParam, startParam, endParam])

  const [search, setSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [userInputValue, setUserInputValue] = useState('')
  const [options, setOptions] = useState<{ id: string; name: string }[]>([])
  const [userOptions, setUserOptions] = useState<{ id: string; name: string }[]>([])
  const [serviceCache, setServiceCache] = useState<{ id: string; name: string }[]>([])
  const [userCache, setUserCache] = useState<{ id: string; name: string }[]>([])

  const selectedServiceIDs = serviceIDStr ? serviceIDStr.split(',') : []
  const selectedUserIDs = userIDStr ? userIDStr.split(',') : []
  const selectedSeverities = severityStr ? severityStr.split(',') : []

  // Fetch initial services for name lookup
  const [initialData] = useQuery({
    query: SERVICES_QUERY,
    variables: { search: '' },
    pause: false, // Always fetch to get service names for chips
  })

  // Fetch initial users for name lookup
  const [initialUserData] = useQuery({
    query: USERS_QUERY,
    variables: { input: { first: 100 } },
    pause: false,
  })

  // Fetch search results
  const [searchData] = useQuery({
    query: SERVICES_QUERY,
    variables: { search },
    pause: !search,
  })

  // Fetch user search results
  const [userSearchData] = useQuery({
    query: USERS_QUERY,
    variables: { input: { search: userSearch, first: 100 } },
    pause: !userSearch,
  })

  // Update service cache (for name lookups in chips)
  useEffect(() => {
    let allServices: { id: string; name: string }[] = [...serviceCache]

    // Add services from initial data
    if (initialData.data?.services?.nodes) {
      allServices = [...allServices, ...initialData.data.services.nodes]
    }

    // Add services from search results
    if (searchData.data?.services?.nodes) {
      allServices = [...allServices, ...searchData.data.services.nodes]
    }

    // Remove duplicates
    const uniqueServices = Array.from(new Set(allServices.map(s => s.id)))
      .map(id => allServices.find(s => s.id === id)!)

    setServiceCache(uniqueServices)
  }, [initialData.data, searchData.data])

  // Update user cache
  useEffect(() => {
    let allUsers: { id: string; name: string }[] = [...userCache]

    if (initialUserData.data?.users?.nodes) {
      allUsers = [...allUsers, ...initialUserData.data.users.nodes]
    }

    if (userSearchData.data?.users?.nodes) {
      allUsers = [...allUsers, ...userSearchData.data.users.nodes]
    }

    const uniqueUsers = Array.from(new Set(allUsers.map(u => u.id)))
      .map(id => allUsers.find(u => u.id === id)!)

    setUserCache(uniqueUsers)
  }, [initialUserData.data, userSearchData.data])

  // Update dropdown options (only show search results when searching)
  useEffect(() => {
    if (search && searchData.data?.services?.nodes) {
      // Only show search results, filtering out selected ones
      setOptions(
        searchData.data.services.nodes.filter(
          (n: { id: string }) => !selectedServiceIDs.includes(n.id),
        ),
      )
    } else {
      // Show empty when not searching
      setOptions([])
    }
  }, [search, searchData.data, selectedServiceIDs.join(',')])

  // Update user dropdown options
  useEffect(() => {
    if (userSearch && userSearchData.data?.users?.nodes) {
      setUserOptions(
        userSearchData.data.users.nodes.filter(
          (n: { id: string }) => !selectedUserIDs.includes(n.id),
        ),
      )
    } else {
      setUserOptions([])
    }
  }, [userSearch, userSearchData.data, selectedUserIDs.join(',')])

  const client = useClient()

  // Fetch missing service names for chips
  useEffect(() => {
    if (!selectedServiceIDs.length) return

    const missingIDs = selectedServiceIDs.filter(id => !serviceCache.find(s => s.id === id))
    if (!missingIDs.length) return

    missingIDs.forEach(async (id) => {
      const result = await client.query(SERVICE_NAME_QUERY, { id }).toPromise()
      if (result.data?.service) {
        setServiceCache(prev => {
          if (prev.some(s => s.id === id)) return prev
          return [...prev, result.data.service]
        })
      }
    })
  }, [selectedServiceIDs.join(','), serviceCache])

  // Fetch missing user names for chips
  useEffect(() => {
    if (!selectedUserIDs.length) return

    const missingIDs = selectedUserIDs.filter(id => !userCache.find(u => u.id === id))
    if (!missingIDs.length) return

    missingIDs.forEach(async (id) => {
      const result = await client.query(USER_NAME_QUERY, { id }).toPromise()
      if (result.data?.user) {
        setUserCache(prev => {
          if (prev.some(u => u.id === id)) return prev
          return [...prev, result.data.user]
        })
      }
    })
  }, [selectedUserIDs.join(','), userCache])

  // Handle input change with debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(inputValue)
    }, 250)

    return () => clearTimeout(t)
  }, [inputValue])

  useEffect(() => {
    const t = setTimeout(() => {
      setUserSearch(userInputValue)
    }, 250)

    return () => clearTimeout(t)
  }, [userInputValue])

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason !== 'input') return
    setInputValue(newInputValue)
  }

  const handleUserInputChange = (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason !== 'input') return
    setUserInputValue(newInputValue)
  }

  // Derived objects for Autocomplete
  const selectedServices = useMemo(() => selectedServiceIDs.map((id) => {
    const s = serviceCache.find((svc) => svc.id === id)
    return s || { id, name: 'Loading...' }
  }), [selectedServiceIDs, serviceCache])

  const selectedUsers = useMemo(() => selectedUserIDs.map((id) => {
    const u = userCache.find((user) => user.id === id)
    return u || { id, name: 'Loading...' }
  }), [selectedUserIDs, userCache])

  const selectedSeverityObjs = useMemo(() => selectedSeverities.map((val) => {
    const s = severityOptions.find((opt) => opt.value === val)
    return s || { value: val, label: val }
  }), [selectedSeverities])

  const handleServiceChange = (event: React.SyntheticEvent, newValue: any[]) => {
    // Filter out any string values (from freeSolo) and only keep service objects
    const serviceObjects = newValue.filter(v => typeof v === 'object' && v !== null && v.id)
    setServiceIDStr(serviceObjects.map((s) => s.id).join(','))
  }

  const handleUserChange = (event: React.SyntheticEvent, newValue: any[]) => {
    const userObjects = newValue.filter(v => typeof v === 'object' && v !== null && v.id)
    setUserIDStr(userObjects.map((u) => u.id).join(','))
  }

  const handleSeverityChange = (event: React.SyntheticEvent, newValue: any[]) => {
    setSeverityStr(newValue.map((s) => s.value).join(','))
  }

  const removeService = (id: string) => {
    setServiceIDStr(selectedServiceIDs.filter((sid) => sid !== id).join(','))
  }

  const removeUser = (id: string) => {
    setUserIDStr(selectedUserIDs.filter((uid) => uid !== id).join(','))
  }

  const removeSeverity = (val: string) => {
    setSeverityStr(selectedSeverities.filter((s) => s !== val).join(','))
  }

  const handleFilterClick = () => {
    setParams({
      interval,
      services: serviceIDStr,
      users: userIDStr,
      severities: severityStr,
      range,
      start: startStr,
      end: endStr,
    })
  }

  const handleReset = () => {
    setInterval('P1D')
    setServiceIDStr('')
    setUserIDStr('')
    setSeverityStr('')
    setRange('P1M')
    setStartStr('')
    setEndStr('')
  }

  return (
    <Box sx={{ marginLeft: '1rem', pt: 3, pr: 2 }}>
      <Grid container spacing={2}>
        {!serviceID && (
          <Grid item xs={12} md={userID ? 6 : 4}>
            <Autocomplete
              key="service-search"
              multiple
              freeSolo
              options={options}
              getOptionLabel={(option: any) => option.name || option.id}
              value={selectedServices}
              onChange={handleServiceChange}
              onInputChange={handleInputChange}
              filterOptions={filterOptions}
              disableCloseOnSelect
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              loading={!!search && searchData.fetching}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Services'
                  placeholder='Search...'
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && inputValue === '') {
                        e.stopPropagation()
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {!!search && searchData.fetching ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderTags={() => null}
              noOptionsText={!inputValue ? 'Type to search' : 'No services found'}
            />
          </Grid>
        )}

        {!userID && (
          <Grid item xs={12} md={serviceID ? 6 : 4}>
            <Autocomplete
              key="user-search"
              multiple
              freeSolo
              options={userOptions as any}
              getOptionLabel={(option: any) => option.name || option.id}
              value={selectedUsers}
              onChange={handleUserChange}
              onInputChange={handleUserInputChange}
              filterOptions={filterOptions}
              disableCloseOnSelect
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              loading={!!userSearch && userSearchData.fetching}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Users'
                  placeholder='Search...'
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && userInputValue === '') {
                        e.stopPropagation()
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {!!userSearch && userSearchData.fetching ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderTags={() => null}
              noOptionsText={!userInputValue ? 'Type to search' : 'No users found'}
            />
          </Grid>
        )}

        <Grid item xs={12} md={!serviceID && !userID ? 4 : (serviceID && userID ? 12 : 6)}>
          <Autocomplete
            multiple
            options={severityOptions}
            getOptionLabel={(option) => option.label}
            value={selectedSeverityObjs}
            onChange={handleSeverityChange}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField {...params} label='Severity' placeholder='Select severity...' />
            )}
            renderTags={() => null}
          />
        </Grid>

        <Grid item xs={12} md={range === 'custom' ? 4 : 6}>
          <FormControl fullWidth>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={range}
              label='Date Range'
              onChange={(e) => {
                const val = e.target.value as string
                if (val === 'custom') {
                    setRange(val)
                    setStartStr(DateTime.now().startOf('month').toFormat('yyyy-MM-dd'))
                    setEndStr(DateTime.now().toFormat('yyyy-MM-dd'))
                  return
                }
                // For non-custom ranges, clear any previous custom start/end.
                setRange(val)
                setStartStr('')
                setEndStr('')
              }}
            >
              <MenuItem value='P1W'>Past week</MenuItem>
              <MenuItem value='P2W'>Past 2 weeks</MenuItem>
              <MenuItem value='P1M'>Past Month</MenuItem>
              <MenuItem value='P3M'>Past 3 Months</MenuItem>
              <MenuItem value='P6M'>Past 6 Months</MenuItem>
              <MenuItem value='P1Y'>Past Year</MenuItem>
              <MenuItem value='custom'>Custom</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {range === 'custom' && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Start Date'
                type='date'
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startStr}
                onChange={(e) => setStartStr(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='End Date'
                type='date'
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endStr}
                onChange={(e) => setEndStr(e.target.value)}
              />
            </Grid>
          </>
        )}

        {range !== 'custom' && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Interval</InputLabel>
              <Select
                value={interval}
                label='Interval'
                onChange={(e) => setInterval(e.target.value as string)}
              >
                <MenuItem value='P1D'>Day</MenuItem>
                <MenuItem value='P1W'>Week</MenuItem>
                <MenuItem value='P1M'>Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleReset} color='inherit'>
            Reset
          </Button>
          <Button
            onClick={handleFilterClick}
            variant='contained'
            disabled={
              interval === ivlParam &&
              serviceIDStr === serviceIDParam &&
              userIDStr === userIDParam &&
              severityStr === severityParam &&
              range === rangeParam &&
              startStr === startParam &&
              endStr === endParam
            }
          >
            Filter
          </Button>
        </Grid>
      </Grid>

      {(selectedServices.length > 0 || selectedUsers.length > 0 || selectedSeverityObjs.length > 0) && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {selectedServices.length > 0 && !serviceID && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant='body2' color='text.secondary'>
                Services:
              </Typography>
              {selectedServices.map((s) => (
                <Chip
                  key={s.id}
                  label={s.name}
                  onDelete={() => removeService(s.id)}
                  size='small'
                />
              ))}
            </Box>
          )}

          {selectedUsers.length > 0 && !userID && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant='body2' color='text.secondary'>
                Users:
              </Typography>
              {selectedUsers.map((u) => (
                <Chip
                  key={u.id}
                  label={u.name}
                  onDelete={() => removeUser(u.id)}
                  size='small'
                />
              ))}
            </Box>
          )}

          {selectedSeverityObjs.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant='body2' color='text.secondary'>
                Severity:
              </Typography>
              {selectedSeverityObjs.map((s) => (
                <Chip
                  key={s.value}
                  label={s.label}
                  onDelete={() => removeSeverity(s.value)}
                  size='small'
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
